import type { SearchSelectOption } from "@/components/SearchSelect";

import type { Department } from "./departments-api";
import type { SiteSummary } from "./sites-api";

/**
 * Departments as `SearchSelect` options (FEAT-2026-028). The label is the
 * unit's own name and the context its parents, so "Jabatan Pengajian Umum"
 * under Alor Setar and under Bangi stay distinguishable, and typing either a
 * campus or a unit finds it.
 *
 * `includesSubunits` is for filters: picking a parent there includes every
 * unit under it (BR-076), so the list says so. A form field that assigns one
 * unit leaves it off.
 */
export function departmentOptions(
  departments: Pick<Department, "id" | "name" | "pathNames" | "counts">[],
  { includesSubunits = false }: { includesSubunits?: boolean } = {},
): SearchSelectOption[] {
  return departments.map((d) => {
    const parents = d.pathNames?.length ? d.pathNames.slice(0, -1) : [];
    return {
      value: d.id,
      label: d.name,
      context: parents.length ? parents.join(" › ") : undefined,
      note: includesSubunits && (d.counts?.children ?? 0) > 0 ? "Includes sub-units" : undefined,
    };
  });
}

/** Sites as `SearchSelect` options: found by name or code. */
export function siteOptions(sites: Pick<SiteSummary, "id" | "name" | "code">[]): SearchSelectOption[] {
  return sites.map((s) => ({ value: s.id, label: s.name, keywords: s.code }));
}

const haystack = (o: SearchSelectOption) => [o.label, o.context, o.keywords].filter(Boolean).join(" ").toLowerCase();

/** True when every typed word appears somewhere in the option, in any order. */
export function matchesQuery(option: SearchSelectOption, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = haystack(option);
  return words.every((w) => text.includes(w));
}

/** "All sites", or the picked options' names, for an export's header (FEAT-2026-030). */
export function pickedLabel(values: string[], options: SearchSelectOption[], allLabel: string): string {
  if (values.length === 0) return allLabel;
  const names = new Map(options.map((o) => [o.value, o.label]));
  return values.map((v) => names.get(v) ?? v).join(", ");
}

/** A filter's picked ids for a query: undefined when nothing is picked. */
export const pickedIds = (values: string[]): string[] | undefined => (values.length ? values : undefined);
